import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Input, Button, List, Radio, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

const { Search } = Input;

const AssignUserModal = ({
  open,
  onClose,
  title = 'Assign Users',
  users = [],
  assignedUsers = [],
  selectedCustomerId,
  onAssign,
  loading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Update selectedUsers when assignedUsers changes or modal opens
  useEffect(() => {
    if (open) {
      const newSelectedUsers = [];
      
      if (Array.isArray(assignedUsers)) {
        assignedUsers.forEach(user => {
          // Only use the explicit id field; if primitive assume it's already an id
          const userId = typeof user === 'object' ? user?.id : user;
          if (userId != null) {
            newSelectedUsers.push(String(userId));
          }
        });
      }
      
      setSelectedUsers(newSelectedUsers);
    }
  }, [assignedUsers, open]);
  
  // Memoize the filtered users to prevent unnecessary recalculations
  const normalizedUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    return users.map(user => {
      if (!user || typeof user !== 'object') return user;
      const normalizedId = user?.id ?? user?.user_id ?? user?._id ?? user?.email;
      return { ...user, id: normalizedId };
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return normalizedUsers;
    
    const searchLower = searchTerm.toLowerCase();
    return normalizedUsers.filter(user => 
      (user.name && user.name.toLowerCase().includes(searchLower)) ||
      (user.full_name && user.full_name.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower))
    );
  }, [searchTerm, normalizedUsers]);

  const handleToggleUser = useCallback((userId) => {
    // Skip if no user ID provided
    if (!userId) {
      console.error('No user ID/email provided to handleToggleUser');
      return;
    }
    
    // Normalize the user ID (object with id or primitive id)
    const normalizedId = String(userId?.id ?? userId);
    if (!normalizedId) {
      return;
    }
    
    // For radio button behavior, always set to the single selected user
    setSelectedUsers([normalizedId]);
  }, []);

  const handleAssign = useCallback(async () => {
    if (!selectedCustomerId) {
      message.error('Please select a customer first');
      return;
    }
    
    if (!Array.isArray(selectedUsers) || selectedUsers.length === 0) {
      message.warning('Please select at least one user to assign');
      return;
    }
    
    // Ensure all user IDs are strings for consistency
    const userIdsToAssign = [...new Set(selectedUsers.map(String).filter(Boolean))];
    
    if (userIdsToAssign.length === 0) {
      message.error('No valid users selected');
      return;
    }

    try {
      
      // Call the parent's onAssign handler with a single user ID
      await onAssign(userIdsToAssign[0]);
      
      // Close the modal on success
      onClose();
    } catch (error) {
      console.error('Error in handleAssign:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to assign users';
      message.error(`Error: ${errorMessage}`);
    }
  }, [selectedCustomerId, selectedUsers, onAssign, onClose]);

  const handleClose = useCallback(() => {
    setSearchTerm('');
    // Reset to the original assigned users when closing
    const resetUsers = Array.isArray(assignedUsers) 
      ? assignedUsers
          .map(u => (typeof u === 'object' ? u?.id : u))
          .filter(Boolean)
          .map(id => String(id))
      : [];
    
    setSelectedUsers(resetUsers);
    onClose();
  }, [assignedUsers, onClose]);

  
  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="assign"
          type="primary"
          onClick={handleAssign}
          loading={loading}
        >
          {loading ? 'Assigning...' : 'Assign Selected'}
        </Button>,
      ]}
      width={500}
      centered
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        <Search
          placeholder="Search users by name or email..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          enterButton={false}
          style={{ marginBottom: 16 }}
          size="middle"
          //onSearch={(value) => setSearchTerm(value)}
        />

        <div style={{ marginBottom: 2 }}>
          <Typography.Text type="secondary">
            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
          </Typography.Text>
        </div>
        <List
          size="small"
          dataSource={filteredUsers}
          style={{ maxHeight: '50vh', overflow: 'auto' }}
          locale={{ emptyText: 'No users found' }}
          rowKey={(user) => user?.id ?? user?.email ?? user?.full_name}
          renderItem={(user) => {
            if (!user) {
              console.error('Invalid user in render:', user);
              return null;
            }
            // Log user details for debugging (show normalized values to avoid undefineds in console)
            
            const userId = user?.id;
            const userEmail = user?.email;
            if (!userId) {
              console.warn('User object missing id; selection disabled for this user:', user);
            }
            const isSelected = userId ? selectedUsers.includes(String(userId)) : false;
            return (
              <List.Item
                key={String(userId ?? userEmail ?? user.full_name)}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('User object:', user);
                  if (!user || !userId) {
                    console.warn('Cannot select user without id:', user);
                    return;
                  }
                  
                  handleToggleUser(userId);
                }}
                style={{
                  cursor: 'pointer',
                  padding: '8px 0',
                  backgroundColor: isSelected ? '#f0f7ff' : 'transparent',
                  transition: 'background-color 0.2s'
                }}
              >
                <Radio 
                  checked={isSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => userId && handleToggleUser(userId)}
                  disabled={!userId}
                />
                <div style={{ marginLeft: 16, flex: 1 }}>
                  <div style={{ fontWeight: isSelected ? 500 : 'normal' }}>{user.name || user.full_name || userEmail}</div>
                  <Typography.Text type="secondary" style={{ fontSize: '0.75rem', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* {user.id && <span>ID: {user.id}</span>} */}
                    {/* {user.full_name && <span>Name: {user.full_name}</span>} */}
                    {user.email && <span>Email: {user.email}</span>}
                  </Typography.Text>
                </div>
              </List.Item>
            );
          }}
        />
      </div>
    </Modal>
  );
};

export default AssignUserModal;